import FormSelect from '../../../../shared/FormSelect';
import {
    Box,
    Chip,
    Grid,
    InputLabel,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Check } from '@mui/icons-material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
    useGetJobsQuery,
    useGetRecurringJobsQuery,
} from '../../../../../app/Slices/SevenDayApiSlice';
import type { Job } from '../../../../../app/Slices/SevenDayApiTypes';
import UserRole from '../../../../../shared/AuthRoles';
import { camelCaseToTitleCase } from '../../../../../shared/utils';
import { ContractorRole } from '../../../../enums/users';
import {
    optionLeadingIcon,
    serviceTypeLeadingIcon,
} from '../../../Contractors/contractorServiceIcons';
import { ProfileShellFormCell } from '../../ProfileReadOnlyField';
import CustomServiceFeedback from './CustomServiceFeedback';
import ServicesOfferedAutocomplete from './ServicesOfferedAutocomplete';
import { useCustomServiceValidation } from './useCustomServiceValidation';

/** Job statuses that mean the contractor still has an open commitment for this service. */
const SERVICE_TYPE_CHANGE_BLOCKING_JOB_STATUSES = new Set<string>([
    'pending',
    'accepted',
    'pendingCompletion',
]);

const shellReadOnlyLabelSx = {
    color: 'text.secondary',
    fontWeight: 500,
    letterSpacing: 0.02,
} as const;

/** Compact read-only service chips + type card (mint pill / dark teal ink). */
const SHELL_SERVICE_MINT_BG = '#E6F4F1';
const SHELL_SERVICE_TEAL = '#2D5A5A';
const READ_ONLY_OFFERED_CHIP_CAP = 5;

interface ServicesProps {
    isEditing: boolean;
    compactShell?: boolean;
}

const Services = ({ isEditing, compactShell = false }: ServicesProps) => {
    const theme = useTheme();
    const { control, setValue, getValues } = useFormContext();
    const loggedInUser = UserRole();
    const isContractorProfileUser =
        loggedInUser?.role === ContractorRole.Admin ||
        loggedInUser?.role === ContractorRole.User;
    const skipJobQueries = !isContractorProfileUser || !loggedInUser?.company;

    const { data: contractorJobs = [] } = useGetJobsQuery(
        {},
        { skip: skipJobQueries },
    );
    const { data: enabledRecurringJobs = [] } = useGetRecurringJobsQuery(
        { enabled: true },
        { skip: skipJobQueries },
    );

    const type = useWatch({ name: 'services.type', control, defaultValue: '' });
    const itemsW = useWatch({ name: 'services.items', control }) as
        | string[]
        | undefined;

    const [
        readOnlyServicesOfferedExpanded,
        setReadOnlyServicesOfferedExpanded,
    ] = useState(false);

    const servicesOfferedItemsKey = useMemo(
        () => JSON.stringify(itemsW ?? []),
        [itemsW],
    );
    useEffect(() => {
        setReadOnlyServicesOfferedExpanded(false);
    }, [servicesOfferedItemsKey]);

    const serviceTypeChangeLocked = useMemo(() => {
        const t = typeof type === 'string' ? type : '';
        if (!t) return false;

        const hasBlockingJob = (contractorJobs as Job[]).some(
            (job) =>
                SERVICE_TYPE_CHANGE_BLOCKING_JOB_STATUSES.has(
                    String(job.status),
                ) && job.serviceType === t,
        );
        const hasActiveRecurring = enabledRecurringJobs.length > 0;

        return hasBlockingJob || hasActiveRecurring;
    }, [contractorJobs, enabledRecurringJobs, type]);

    const serviceTypeLockHelperText =
        'Service type can’t be changed while you have active jobs or recurring bookings for your organisation.';

    const prevServiceTypeRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!isEditing) {
            prevServiceTypeRef.current = type || undefined;
            return;
        }
        const prev = prevServiceTypeRef.current;
        if (prev !== undefined && prev !== type && type !== '') {
            setValue('services.items', [], { shouldDirty: true });
        }
        prevServiceTypeRef.current = type || undefined;
    }, [isEditing, type, setValue]);

    const cleaningOptions = [
        { value: 'regularClean', label: 'Regular Clean' },
        { value: 'deepClean', label: 'Deep Clean' },
        { value: 'changeLinen', label: 'Change Linen' },
        { value: 'outdoorCleaning', label: 'Outdoor Cleaning' },
        { value: 'bbqCleaning', label: 'BBQ Cleaning' },
        { value: 'windowCleaning', label: 'Window Cleaning' },
        { value: 'carpetCleaning', label: 'Carpet Cleaning' },
        { value: 'linenWashing', label: 'Linen Washing' },
    ];

    const cateringOptions = [
        { value: 'customMenu', label: 'Custom Menu' },
        { value: 'setMenu', label: 'Set Menu' },
        { value: 'foodStation', label: 'Food Station' },
        { value: 'dietaryRequests', label: 'Dietary Requests' },
        { value: 'alcoholService', label: 'Alcohol Service' },
        { value: 'onsiteChef', label: 'Onsite Chef' },
        { value: 'equipment', label: 'Equipment' },
        { value: 'delivery', label: 'Delivery' },
        { value: 'cleanup', label: 'Cleanup' },
    ];

    const alcoholOptions = [
        { value: 'fullBar', label: 'Full Bar' },
        { value: 'signatureCocktails', label: 'Signature Cocktails' },
        { value: 'bartenderService', label: 'Bartender Service' },
        { value: 'alcoholCatering', label: 'Alcohol Catering' },
        { value: 'wineAndBeerTasting', label: 'Wine and Beer Tasting' },
        { value: 'nonAlcoholicOptions', label: 'Non Alcoholic Options' },
        { value: 'cocktailStations', label: 'Cocktail Stations' },
        { value: 'iceServices', label: 'Ice Services' },
        { value: 'alocholDelivery', label: 'Alcohol Delivery' },
        { value: 'mobileAndOutdoorBars', label: 'Mobile and Outdoor Bars' },
        { value: 'postEventCleanUp', label: 'Post Event Clean Up' },
    ];
    const workshopOptions = [
        { value: 'candleMaking', label: 'Candle Making' },
        { value: 'pottery', label: 'Pottery' },
        { value: 'artClass', label: 'Art Class' },
        { value: 'cookingClass', label: 'Cooking Class' },
        { value: 'cocktailClass', label: 'Cocktail Class' },
    ];

    const wellnessOptions = [
        { value: 'massage', label: 'Massage' },
        { value: 'pilates', label: 'Pilates' },
        { value: 'yoga', label: 'Yoga' },
        { value: 'meditation', label: 'Meditation' },
        { value: 'soundHealing', label: 'Sound Healing' },
    ];

    const ItemMapping: { [key: string]: { value: string; label: string }[] } = {
        cleaning: cleaningOptions,
        catering: cateringOptions,
        alcohol: alcoholOptions,
        workshop: workshopOptions,
        wellness: wellnessOptions,
        other: [],
        '': [],
    };

    const serviceTypeOptions = [
        { label: 'Cleaning', value: 'cleaning' },
        { label: 'Catering', value: 'catering' },
        { label: 'Alcohol', value: 'alcohol' },
        { label: 'Workshop', value: 'workshop' },
        { label: 'Wellness', value: 'wellness' },
        { label: 'Other', value: 'other' },
    ];

    const optionsForType = ItemMapping[type] ?? [];

    const {
        validate: validateCustomService,
        acceptSuggestion: acceptCustomServiceCorrection,
        validating: validatingCustomService,
        rejection: customServiceRejection,
        suggestion: customServiceSuggestion,
        clearFeedback: clearCustomServiceFeedback,
    } = useCustomServiceValidation(type, optionsForType);

    const acceptCustomServiceSuggestion = async () => {
        if (!customServiceSuggestion) return;

        const verdict = await acceptCustomServiceCorrection();
        if (!verdict.accepted) return;

        const currentItems =
            (getValues('services.items') as string[] | undefined) ?? [];
        const exists = currentItems.some(
            (item) => item.toLowerCase() === verdict.label.toLowerCase(),
        );

        if (!exists) {
            setValue('services.items', [...currentItems, verdict.label], {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
        clearCustomServiceFeedback();
    };

    if (compactShell) {
        const typeLabel =
            serviceTypeOptions.find((o) => o.value === type)?.label ?? type;
        const ServiceTypeIcon = serviceTypeLeadingIcon(type);

        const offeredItems = itemsW ?? [];
        const offeredShown = offeredItems.slice(0, READ_ONLY_OFFERED_CHIP_CAP);
        const offeredOverflow =
            offeredItems.length > READ_ONLY_OFFERED_CHIP_CAP
                ? offeredItems.length - READ_ONLY_OFFERED_CHIP_CAP
                : 0;

        const readOnlyOfferedChipSx = {
            flexShrink: 0,
            height: 'auto',
            px: 1,
            py: 0.5,
            borderRadius: 999,
            border: `1px solid ${SHELL_SERVICE_TEAL}`,
            bgcolor: SHELL_SERVICE_MINT_BG,
            color: SHELL_SERVICE_TEAL,
            fontWeight: 600,
            letterSpacing: 0.01,
            '& .MuiChip-icon': {
                color: SHELL_SERVICE_TEAL,
                ml: 0.5,
            },
            '& .MuiChip-label': {
                px: 0.75,
                py: 0.25,
                typography: 'body2',
                fontSize: theme.typography.body2.fontSize,
            },
        };

        const readOnlyOfferedOverflowChipSx = {
            flexShrink: 0,
            height: 'auto',
            px: 1,
            py: 0.5,
            borderRadius: 999,
            border: 'none',
            boxShadow: 'none',
            bgcolor: alpha(theme.palette.grey[500], 0.14),
            color: theme.palette.text.secondary,
            fontWeight: 600,
            letterSpacing: 0.01,
            cursor: 'pointer',
            '& .MuiChip-label': {
                px: 0.75,
                py: 0.25,
                typography: 'body2',
                fontSize: theme.typography.body2.fontSize,
            },
        };

        const offeredChipsToShow = readOnlyServicesOfferedExpanded
            ? offeredItems
            : offeredShown;

        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 0,
                    border: 'none',
                    boxShadow: 'none',
                    bgcolor: 'transparent',
                    p: 0,
                }}
            >
                <Stack spacing={2.5}>
                    {!isEditing ? (
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                            sx={{ width: '100%', minWidth: 0 }}
                        >
                            <Paper
                                variant='outlined'
                                elevation={0}
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(SHELL_SERVICE_TEAL, 0.35)}`,
                                    p: 1.75,
                                    bgcolor: SHELL_SERVICE_MINT_BG,
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    flexShrink: 0,
                                    width: { xs: '100%', sm: 'auto' },
                                    maxWidth: { sm: 320 },
                                    alignSelf: { xs: 'stretch' },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        bgcolor: 'common.white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
                                    }}
                                >
                                    <ServiceTypeIcon
                                        sx={{
                                            fontSize: 26,
                                            color: SHELL_SERVICE_TEAL,
                                        }}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant='caption'
                                        component='div'
                                        sx={shellReadOnlyLabelSx}
                                    >
                                        Service type
                                    </Typography>
                                    <Typography
                                        variant='body1'
                                        sx={{
                                            fontWeight: 700,
                                            lineHeight: 1.35,
                                            mt: 0.5,
                                            color: SHELL_SERVICE_TEAL,
                                        }}
                                    >
                                        {typeLabel || '—'}
                                    </Typography>
                                </Box>
                            </Paper>

                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    /* Nudge down so "Services offered" lines up with "Service type" in the padded type card. */
                                    pt: { xs: 0, sm: 2 },
                                }}
                            >
                                <Typography
                                    variant='caption'
                                    component='div'
                                    sx={shellReadOnlyLabelSx}
                                >
                                    Services offered
                                </Typography>
                                {itemsW?.length ? (
                                    <Stack
                                        direction='row'
                                        gap={1}
                                        flexWrap='wrap'
                                        sx={{ mt: 0.5 }}
                                    >
                                        {offeredChipsToShow.map((v) => {
                                            const label =
                                                v === 'bbqCleaning'
                                                    ? 'BBQ Cleaning'
                                                    : camelCaseToTitleCase(v);
                                            const OptIcon =
                                                optionLeadingIcon(v);
                                            return (
                                                <Chip
                                                    key={v}
                                                    icon={
                                                        <OptIcon
                                                            sx={{
                                                                fontSize: 18,
                                                                color: SHELL_SERVICE_TEAL,
                                                            }}
                                                        />
                                                    }
                                                    label={label}
                                                    size='small'
                                                    sx={readOnlyOfferedChipSx}
                                                    variant='outlined'
                                                />
                                            );
                                        })}
                                        {offeredOverflow > 0 &&
                                        !readOnlyServicesOfferedExpanded ? (
                                            <Chip
                                                key='__services_offered_more__'
                                                component='button'
                                                type='button'
                                                label={`+${offeredOverflow}`}
                                                size='small'
                                                sx={
                                                    readOnlyOfferedOverflowChipSx
                                                }
                                                variant='filled'
                                                onClick={() =>
                                                    setReadOnlyServicesOfferedExpanded(
                                                        true,
                                                    )
                                                }
                                                aria-expanded={false}
                                                aria-label={`Show ${offeredOverflow} more services offered`}
                                            />
                                        ) : null}
                                        {offeredOverflow > 0 &&
                                        readOnlyServicesOfferedExpanded ? (
                                            <Chip
                                                key='__services_offered_less__'
                                                component='button'
                                                type='button'
                                                label='Show less'
                                                size='small'
                                                sx={
                                                    readOnlyOfferedOverflowChipSx
                                                }
                                                variant='filled'
                                                onClick={() =>
                                                    setReadOnlyServicesOfferedExpanded(
                                                        false,
                                                    )
                                                }
                                                aria-expanded
                                                aria-label='Show fewer services offered'
                                            />
                                        ) : null}
                                    </Stack>
                                ) : (
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ mt: 0.5 }}
                                    >
                                        —
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    ) : (
                        <>
                            <ProfileShellFormCell
                                label='1. Service type'
                                labelAdornment={
                                    <Box
                                        component='span'
                                        sx={{
                                            color: 'error.main',
                                            fontWeight: 600,
                                        }}
                                        aria-hidden
                                    >
                                        *
                                    </Box>
                                }
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxWidth: { xs: '100%', sm: '33%' },
                                    }}
                                >
                                    <FormSelect
                                        name='services.type'
                                        control={control}
                                        options={serviceTypeOptions}
                                        disabled={
                                            !isEditing ||
                                            serviceTypeChangeLocked
                                        }
                                    />
                                    {serviceTypeChangeLocked && isEditing ? (
                                        <Typography
                                            variant='caption'
                                            color='text.secondary'
                                            sx={{
                                                mt: 1,
                                                display: 'block',
                                                maxWidth: 360,
                                            }}
                                        >
                                            {serviceTypeLockHelperText}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </ProfileShellFormCell>

                            <ProfileShellFormCell
                                label={
                                    <>
                                        2. What services do you provide?{' '}
                                        <Box
                                            component='span'
                                            sx={{
                                                color: 'error.main',
                                                fontWeight: 600,
                                            }}
                                            aria-hidden
                                        >
                                            *
                                        </Box>
                                    </>
                                }
                            >
                                <Stack spacing={1}>
                                    <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ letterSpacing: 0.02 }}
                                    >
                                        {type === 'other'
                                            ? 'Add the services you provide.'
                                            : 'Select all services that apply, or add your own.'}
                                    </Typography>

                                    {!type ? (
                                        <Typography
                                            variant='body2'
                                            color='text.secondary'
                                        >
                                            Choose a service type first.
                                        </Typography>
                                    ) : (
                                        <>
                                            {optionsForType.length > 0 ? (
                                                <Controller
                                                    name='services.items'
                                                    control={control}
                                                    render={({ field }) => {
                                                        const selected: string[] =
                                                            Array.isArray(
                                                                field.value,
                                                            )
                                                                ? field.value
                                                                : [];

                                                        const toggle = (
                                                            value: string,
                                                        ) => {
                                                            const next =
                                                                selected.includes(
                                                                    value,
                                                                )
                                                                    ? selected.filter(
                                                                          (x) =>
                                                                              x !==
                                                                              value,
                                                                      )
                                                                    : [
                                                                          ...selected,
                                                                          value,
                                                                      ];
                                                            field.onChange(
                                                                next,
                                                            );
                                                        };

                                                        return (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'grid',
                                                                    gridTemplateColumns:
                                                                        {
                                                                            xs: '1fr',
                                                                            sm: 'repeat(2, 1fr)',
                                                                            md: 'repeat(3, 1fr)',
                                                                        },
                                                                    gap: 1.5,
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                {optionsForType.map(
                                                                    (opt) => {
                                                                        const on =
                                                                            selected.includes(
                                                                                opt.value,
                                                                            );
                                                                        const OptIcon =
                                                                            optionLeadingIcon(
                                                                                opt.value,
                                                                            );
                                                                        return (
                                                                            <Box
                                                                                component='button'
                                                                                type='button'
                                                                                key={
                                                                                    opt.value
                                                                                }
                                                                                onClick={() =>
                                                                                    toggle(
                                                                                        opt.value,
                                                                                    )
                                                                                }
                                                                                sx={{
                                                                                    display:
                                                                                        'flex',
                                                                                    alignItems:
                                                                                        'center',
                                                                                    gap: 1.25,
                                                                                    p: 1.5,
                                                                                    textAlign:
                                                                                        'left',
                                                                                    cursor: 'pointer',
                                                                                    borderRadius: 2,
                                                                                    border: on
                                                                                        ? `2px solid ${theme.palette.textAccent.main}`
                                                                                        : `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                                                                                    bgcolor:
                                                                                        on
                                                                                            ? theme
                                                                                                  .palette
                                                                                                  .textAccent
                                                                                                  .contrastText
                                                                                            : 'background.paper',
                                                                                    transition:
                                                                                        theme.transitions.create(
                                                                                            [
                                                                                                'border-color',
                                                                                                'background-color',
                                                                                            ],
                                                                                            {
                                                                                                duration:
                                                                                                    theme
                                                                                                        .transitions
                                                                                                        .duration
                                                                                                        .shortest,
                                                                                            },
                                                                                        ),
                                                                                }}
                                                                            >
                                                                                <OptIcon
                                                                                    sx={{
                                                                                        fontSize: 22,
                                                                                        color: on
                                                                                            ? theme
                                                                                                  .palette
                                                                                                  .textAccent
                                                                                                  .main
                                                                                            : 'text.secondary',
                                                                                        flexShrink: 0,
                                                                                    }}
                                                                                />
                                                                                <Typography
                                                                                    variant='body2'
                                                                                    sx={{
                                                                                        flex: 1,
                                                                                        fontWeight:
                                                                                            on
                                                                                                ? 700
                                                                                                : 500,
                                                                                        color: on
                                                                                            ? theme
                                                                                                  .palette
                                                                                                  .textAccent
                                                                                                  .main
                                                                                            : 'text.primary',
                                                                                        lineHeight: 1.35,
                                                                                    }}
                                                                                >
                                                                                    {
                                                                                        opt.label
                                                                                    }
                                                                                </Typography>
                                                                                {on ? (
                                                                                    <Box
                                                                                        sx={{
                                                                                            width: 22,
                                                                                            height: 22,
                                                                                            borderRadius:
                                                                                                '50%',
                                                                                            bgcolor:
                                                                                                theme
                                                                                                    .palette
                                                                                                    .textAccent
                                                                                                    .contrastText,
                                                                                            display:
                                                                                                'flex',
                                                                                            alignItems:
                                                                                                'center',
                                                                                            justifyContent:
                                                                                                'center',
                                                                                            flexShrink: 0,
                                                                                        }}
                                                                                    >
                                                                                        <Check
                                                                                            sx={{
                                                                                                fontSize: 15,
                                                                                                color: 'common.white',
                                                                                            }}
                                                                                        />
                                                                                    </Box>
                                                                                ) : (
                                                                                    <Box
                                                                                        sx={{
                                                                                            width: 22,
                                                                                            height: 22,
                                                                                            borderRadius:
                                                                                                '50%',
                                                                                            border: `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                                                                                            flexShrink: 0,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                        );
                                                                    },
                                                                )}
                                                            </Box>
                                                        );
                                                    }}
                                                />
                                            ) : null}
                                            <ServicesOfferedAutocomplete
                                                name='services.items'
                                                control={control}
                                                options={optionsForType}
                                                excludeValues={
                                                    optionsForType.length > 0
                                                        ? optionsForType.map(
                                                              (o) => o.value,
                                                          )
                                                        : undefined
                                                }
                                                disabled={!type || !isEditing}
                                                placeholder={
                                                    optionsForType.length > 0
                                                        ? 'Add a custom service'
                                                        : type
                                                          ? type === 'other'
                                                              ? 'Type a service and press Enter'
                                                              : 'Select or type a service'
                                                          : 'Select Service Type'
                                                }
                                                onValidateNewValue={
                                                    validateCustomService
                                                }
                                                validating={
                                                    validatingCustomService
                                                }
                                                onInputEdited={
                                                    clearCustomServiceFeedback
                                                }
                                            />
                                            <CustomServiceFeedback
                                                rejection={
                                                    customServiceRejection
                                                }
                                                suggestion={
                                                    customServiceSuggestion
                                                }
                                                validating={
                                                    validatingCustomService
                                                }
                                                onAcceptSuggestion={
                                                    acceptCustomServiceSuggestion
                                                }
                                                onDismiss={
                                                    clearCustomServiceFeedback
                                                }
                                            />
                                        </>
                                    )}
                                </Stack>
                            </ProfileShellFormCell>
                        </>
                    )}
                </Stack>
            </Paper>
        );
    }

    return (
        <>
            <Typography
                sx={{ fontWeight: 'bold', color: 'text.primary', py: '30px' }}
            >
                Services
            </Typography>
            <Grid container columnSpacing='50px' rowSpacing='10px'>
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel required>Service Type</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <FormSelect
                            name='services.type'
                            control={control}
                            options={serviceTypeOptions}
                            disabled={!isEditing || serviceTypeChangeLocked}
                        />
                        {serviceTypeChangeLocked && isEditing ? (
                            <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{ mt: 1, display: 'block', maxWidth: 420 }}
                            >
                                {serviceTypeLockHelperText}
                            </Typography>
                        ) : null}
                    </Grid>
                </Grid>
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel required>Services Offered</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <ServicesOfferedAutocomplete
                            name='services.items'
                            control={control}
                            options={optionsForType}
                            disabled={!type || !isEditing}
                            placeholder={
                                type
                                    ? type === 'other'
                                        ? 'Type a service and press Enter'
                                        : 'Select or type a service'
                                    : 'Select Service Type'
                            }
                            onValidateNewValue={validateCustomService}
                            validating={validatingCustomService}
                            onInputEdited={clearCustomServiceFeedback}
                        />
                        <CustomServiceFeedback
                            rejection={customServiceRejection}
                            suggestion={customServiceSuggestion}
                            validating={validatingCustomService}
                            onAcceptSuggestion={acceptCustomServiceSuggestion}
                            onDismiss={clearCustomServiceFeedback}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default Services;
